import React, { useMemo } from 'react';

import { Chip } from '../../../../../base/components/Chip';
import { Icon } from '../../../../../base/components/Icon';
import { useAppSelector } from '../../../../../base/hooks';
import { __t } from '../../../../../base/utils/t';
import {
    getFilePreviewUrl,
    pluralize,
    getCurrencyById,
    getLanguageById,
    mapRateTypeToString,
} from '../../../../../base/utils';

import './styles.scss';

export const VacancyPreview = () => {
    const {
        fields: {
            name,
            rateType,
            rateFrom,
            rateTo,
            currencyId,
            city,
            workersCount,
            isHotVacancy,
            isExperienceNeeded,
            isAccomodationProvided,
            isClothesProvided,
            isFoodProvided,
            isPassportOnly,
            isRemoteWork,
            isSeasonalWork,
            isTransferProvided,
            suitableForCouple,
            suitableForMen,
            suitableForStudents,
            suitableForWomen,
            requiredLanguageId,
            hoursPerMonthFrom,
            hoursPerMonthTo,
            description,
            images,
            imagesToUpload,
            specialities,
            isQuickApplyAvailable,
        },
    } = useAppSelector((state) => state.vacancyForm);
    const { currency, language } = useAppSelector((state) => state.employerApp.config);

    const currencySign = useMemo(() => getCurrencyById(currency, currencyId)?.sign, [currency, currencyId]);
    const rateTypeText = useMemo(() => mapRateTypeToString(rateType), [rateType]);
    const languageText = useMemo(() => {
        if (requiredLanguageId) {
            return getLanguageById(language, requiredLanguageId);
        }

        return '';
    }, [language, requiredLanguageId]);

    const topLabels = useMemo(() => {
        if (!isHotVacancy) {
            return null;
        }

        return (
            <div className='vacancy-view__chips-wrap'>
                {isQuickApplyAvailable && (
                    <Chip
                        text={__t('Отклик без анкеты')}
                        icon='bolt-solid'
                        iconColor='purple'
                        iconSize='10px'
                        type='purple'
                        className='vacancy-view__chip'
                    />
                )}
                {isHotVacancy && <Chip text={__t('Работа на сейчас')} type='danger' className='vacancy-view__chip' />}
            </div>
        );
    }, [isHotVacancy]);

    const photos = useMemo(() => {
        const hasPhotos = !!images.length || !!imagesToUpload.length;
        if (!hasPhotos) {
            return null;
        }

        return (
            <div className='vacancy-view__photo-preview-wrap'>
                {images.map(({ id, url }) => (
                    <div className='vacancy-view__photo-preview-item' key={id}>
                        <img src={url} />
                    </div>
                ))}
                {imagesToUpload.map(({ id, file }) => (
                    <div className='vacancy-view__photo-preview-item' key={id}>
                        <img src={getFilePreviewUrl(file)} />
                    </div>
                ))}
            </div>
        );
    }, [images, imagesToUpload]);

    const pluralizedWorkersCountTexts = [__t('работник'), __t('работника'), __t('работников')];

    return (
        <section className='vacancy-view'>
            {topLabels}

            <h1 className='vacancy-view__title'>{name}</h1>

            <h1 className='vacancy-view__specialities'>
                {specialities.map((s) => (
                    <span key={s.id} className='vacancy-view__specialities-item'>
                        {s.name}
                    </span>
                ))}
            </h1>

            <p className='vacancy-view__salary'>
                {rateFrom} {rateTo ? `- ${rateTo}` : ''} {currencySign}/{rateTypeText}
            </p>

            <div className='vacancy-view__additional-info-wrap'>
                <div className='vacancy-view__additional-info-item vacancy-view__additional-info-item--active'>
                    <Icon icon='location-dot-light' size='24px' />
                    {city?.country.name}, {city?.name}
                </div>

                <div className='vacancy-view__additional-info-item vacancy-view__additional-info-item--active'>
                    <Icon icon='user-helmet-safety-light' size='24px' />
                    {workersCount} {pluralize(parseInt(workersCount), pluralizedWorkersCountTexts)}
                </div>
            </div>

            <div className='vacancy-view__divider'></div>

            <h2 className='vacancy-view__subtitle'>{__t('Требования')}</h2>

            <div className='vacancy-view__additional-info-wrap vacancy-view__additional-info-wrap--two-row'>
                <div
                    className={`vacancy-view__additional-info-item ${
                        isHotVacancy ? 'vacancy-view__additional-info-item--active' : ''
                    }`}
                >
                    <Icon icon='stopwatch-light' size='24px' />
                    {__t('Нужен работник срочно')}
                </div>

                <div
                    className={`vacancy-view__additional-info-item ${
                        isExperienceNeeded ? 'vacancy-view__additional-info-item--active' : ''
                    }`}
                >
                    <Icon icon='person-circle-check-light' size='24px' color='info' />
                    {__t('Возможно без опыта работы')}
                </div>

                <div
                    className={`vacancy-view__additional-info-item ${
                        isPassportOnly ? 'vacancy-view__additional-info-item--active' : ''
                    }`}
                >
                    <Icon icon='passport-light' size='24px' color='info' />
                    {__t('Возможно по биометрическому паспорту')}
                </div>

                <div
                    className={`vacancy-view__additional-info-item ${
                        languageText ? 'vacancy-view__additional-info-item--active' : ''
                    }`}
                >
                    <Icon icon='language-light' size='24px' />
                    {__t('Знание языка')}
                    {languageText ? `: ${languageText}` : ''}
                </div>
            </div>

            <h2 className='vacancy-view__subtitle vacancy-view--mt-40'>{__t('Условия')}</h2>

            <div className='vacancy-view__additional-info-wrap vacancy-view__additional-info-wrap--two-row'>
                <div
                    className={`vacancy-view__additional-info-item ${
                        isAccomodationProvided ? 'vacancy-view__additional-info-item--active' : ''
                    }`}
                >
                    <Icon icon='bed-light' size='24px' />
                    {__t('Жилье')}
                </div>

                <div
                    className={`vacancy-view__additional-info-item ${
                        isFoodProvided ? 'vacancy-view__additional-info-item--active' : ''
                    }`}
                >
                    <Icon icon='plate-utensils-light' size='24px' color='info' />
                    {__t('Питание')}
                </div>

                <div
                    className={`vacancy-view__additional-info-item ${
                        isClothesProvided ? 'vacancy-view__additional-info-item--active' : ''
                    }`}
                >
                    <Icon icon='shirt-light' size='24px' />
                    {__t('Рабочая одежда')}
                </div>

                <div
                    className={`vacancy-view__additional-info-item ${
                        isTransferProvided ? 'vacancy-view__additional-info-item--active' : ''
                    }`}
                >
                    <Icon icon='car-bus-light' size='24px' />
                    {__t('Трансфер до рабочего места')}
                </div>

                <div
                    className={`vacancy-view__additional-info-item ${
                        isSeasonalWork ? 'vacancy-view__additional-info-item--active' : ''
                    }`}
                >
                    <Icon icon='sun-bright-light' size='24px' />
                    {__t('Сезонная работа')}
                </div>

                <div
                    className={`vacancy-view__additional-info-item ${
                        isRemoteWork ? 'vacancy-view__additional-info-item--active' : ''
                    }`}
                >
                    <Icon icon='house-laptop-light' size='24px' />
                    {__t('Удаленная работа')}
                </div>
            </div>

            <div className='vacancy-view__divider'></div>

            {description && (
                <>
                    <div className='vacancy-view__description' dangerouslySetInnerHTML={{ __html: description }} />
                    <div className='translated-label'>
                        <Icon icon='language-regular' size='14px' color='info' className='translated-label__icon' />
                        {__t('Этот текст  будет переведен на язык работника в автоматическом режиме.')}
                    </div>

                    <div className='vacancy-view__divider'></div>
                </>
            )}

            <h3 className='vacancy-view__subheading'>{__t('Режим работы')}</h3>

            <div className='vacancy-view__additional-info-item vacancy-view__additional-info-item--active'>
                <Icon icon='calendar-clock-light' size='24px' />
                {`${__t('от')} ${hoursPerMonthFrom} ${hoursPerMonthTo ? `${__t('до')} ${hoursPerMonthTo}` : ''} ${__t(
                    'часов в месяц',
                )}`}
            </div>

            <h3 className='vacancy-view__subheading vacancy-view--mt-32'>{__t('Работа подходит')}</h3>

            <div className='vacancy-view__additional-info-wrap'>
                <div
                    className={`vacancy-view__additional-info-item ${
                        suitableForMen ? 'vacancy-view__additional-info-item--active' : ''
                    }`}
                >
                    <Icon icon='person-light' size='24px' />
                    {__t('Мужчинам')}
                </div>

                <div
                    className={`vacancy-view__additional-info-item ${
                        suitableForWomen ? 'vacancy-view__additional-info-item--active' : ''
                    }`}
                >
                    <Icon icon='person-dress-light' size='24px' />
                    {__t('Женщинам')}
                </div>

                <div
                    className={`vacancy-view__additional-info-item ${
                        suitableForCouple ? 'vacancy-view__additional-info-item--active' : ''
                    }`}
                >
                    <Icon icon='people-light' size='24px' />
                    {__t('Семейным парам')}
                </div>

                <div
                    className={`vacancy-view__additional-info-item ${
                        suitableForStudents ? 'vacancy-view__additional-info-item--active' : ''
                    }`}
                >
                    <Icon icon='graduation-cap-light' size='24px' />
                    {__t('Студентам')}
                </div>
            </div>

            {photos}
        </section>
    );
};
